import type {
  FeatureFromGeojson,
  FeatureCollection,
} from "../../FeaturesStore.utils";

/**
 * IssueGeoJsonLayer 증분 렌더(D6)의 순수 로직.
 *
 * geoJson 변경 시 레이어/소스를 통째 재생성하지 않고 id 기준으로 add/remove/update 를 분류하고,
 * 시각(스타일) 변화 여부를 판정한다. OL 사이드이펙트(readFeature/transform/addFeature/changed)는
 * 컴포넌트가 담당하고, 분류·판정 로직만 여기서 순수 함수로 분리해 단위 테스트 가능하게 한다.
 */

/** 스타일 결정에 영향을 주는 properties 키 — 이 값들이 바뀌면 해당 피처만 재드로우한다. */
export const ISSUE_STYLE_KEYS = [
  "type",
  "title",
  "isSelected",
  "isTextShown",
  "fontSize",
  "color",
  "opacity",
] as const;

/** 표시 대상(`isShown === true`) 여부. */
export function isFeatureShown(feature: FeatureFromGeojson): boolean {
  return feature?.properties?.["isShown"] === true;
}

/** isShown=true 인 피처만 추린다(표시 대상). */
export function getShownFeatures(
  geoJson: FeatureCollection | null | undefined
): FeatureFromGeojson[] {
  return (geoJson?.features ?? []).filter(isFeatureShown);
}

export interface IssueFeatureDiff {
  /** 소스에 없던 신규(또는 isShown 으로 새로 보이게 된) 피처 */
  toAdd: FeatureFromGeojson[];
  /** 소스에 이미 존재하는 피처(변경분만 갱신) */
  toUpdate: FeatureFromGeojson[];
  /** 더 이상 표시되지 않아 소스에서 제거할 id */
  toRemoveIds: string[];
}

/**
 * 현재 소스에 존재하는 id 집합과 표시 대상 피처를 id 기준으로 분류한다.
 */
export function computeIssueFeatureDiff(
  presentIds: Iterable<string>,
  desired: FeatureFromGeojson[]
): IssueFeatureDiff {
  const present = presentIds instanceof Set ? presentIds : new Set(presentIds);
  const desiredIds = new Set(desired.map((f) => String(f.id)));

  const toAdd: FeatureFromGeojson[] = [];
  const toUpdate: FeatureFromGeojson[] = [];
  for (const f of desired) {
    if (present.has(String(f.id))) toUpdate.push(f);
    else toAdd.push(f);
  }

  const toRemoveIds: string[] = [];
  for (const id of present) {
    if (!desiredIds.has(id)) toRemoveIds.push(id);
  }

  return { toAdd, toUpdate, toRemoveIds };
}

/**
 * 스타일 결정 속성 중 하나라도 바뀌었는지 — 바뀌면 해당 피처 재드로우가 필요하다.
 */
export function hasStyleRelevantChange(
  prev: Record<string, any> | undefined,
  next: Record<string, any> | undefined
): boolean {
  return ISSUE_STYLE_KEYS.some((key) => prev?.[key] !== next?.[key]);
}

/** 비교용 스타일 속성 스냅샷을 추린다. */
export function pickStyleProps(
  properties: Record<string, any> | undefined
): Record<string, any> {
  const picked: Record<string, any> = {};
  for (const key of ISSUE_STYLE_KEYS) picked[key] = properties?.[key];
  return picked;
}
